'use client';

import { useState, useMemo, useCallback } from 'react';
import { cn } from '@/lib/utils';
import { MessageScrollArea } from '../MessageScrollArea';
import { ChatInput } from '../ChatInput';
import { SubjectField } from '../SubjectField';
import { EditDraftDialog } from '../EditDraftDialog';
import { AddDocument } from '@/components/shared/add-document';
import { useMatterFilesService, useFileUpload } from '@/lib/services/files';
import type { InstitutionCorrespondence } from '../../types';
import type { WorkflowDocument } from '@/lib/services/correspondences/workflows/workflow-types';

// Extracted hooks and components
import {
  useMessageTransform,
  useDraftManagement,
  useAttachments,
  useMessageSending,
  useThreadData,
  useSubjectManagement,
  useLocalDraft,
  useDraftAttachmentSync,
  useDraftEditDialog,
  useDisplayMessages
} from './hooks';
import {
  ChatLoadingSkeleton,
  AttachmentsDisplay,
  EmptyState,
  FullscreenDialog
} from './components';
import { convertDocumentsToWorkflowFormat } from './utils';

// Mock data
import { mockCorrespondenceScenarios } from '../../mocks/correspondence-data';

export interface SimplifiedCorrespondenceChatProps {
  matterId: number;
  institutionId?: number;
  institutionName: string;
  institutionFaviconUrl?: string;
  matterReference?: string;
  deceasedName?: string;
  fullHeight?: boolean;
  externalMessages?: InstitutionCorrespondence[];
  onSendMessage?: (
    content: string,
    subject?: string,
    attachments?: WorkflowDocument[],
    parentCorrespondenceId?: string | number,
    existingDraftId?: number
  ) => void | Promise<void>;
  onSendDraft?: (draftId: string) => Promise<void>;
  onUpdateDraft?: (draftId: string, content: string) => Promise<void>;
  onDeleteDraft?: (draftId: string) => Promise<void>;
  onSaveDraftAttachments?: (
    draftId: number,
    attachments: WorkflowDocument[]
  ) => Promise<void>;
  isInFullScreenDialog?: boolean;
  onCloseFullScreen?: () => void;
  autoFocus?: boolean;
  initialMessage?: string;
  isLoading?: boolean;
}

/**
 * Only live mount in the running app: the `correspondence.create` CTA
 * fallback (`lib/services/tasks/cta-dialog-renderer.tsx:146`, opened via
 * `openCorrespondenceDialog` from `cta-handlers-integrated.ts:358`) — for
 * correspondence CTA types that are neither `notification`, `follow-up`,
 * nor `collection-request`. Every named production correspondence surface
 * (Discovery, Redemption) has moved to the shared Communications
 * `ThreadView` (LF-3511); do not add a new mount of this component without
 * confirming a CTA type still reaches that fallback branch (LF-6290).
 */
export function ChatContainerSimplified({
  matterId,
  institutionId,
  institutionName,
  institutionFaviconUrl,
  matterReference,
  deceasedName,
  fullHeight = false,
  externalMessages,
  onSendMessage: onSendMessageProp,
  onSendDraft: onSendDraftProp,
  onUpdateDraft: onUpdateDraftProp,
  onDeleteDraft: onDeleteDraftProp,
  onSaveDraftAttachments,
  isInFullScreenDialog: _isInFullScreenDialog = false,
  onCloseFullScreen: _onCloseFullScreen,
  autoFocus = false,
  initialMessage = '',
  isLoading = false
}: SimplifiedCorrespondenceChatProps) {
  // UI state
  const [isFullScreen, setIsFullScreen] = useState(false);

  // File services
  const { files: matterFiles, refetch: refetchFiles } =
    useMatterFilesService(matterId);
  const { uploadFile } = useFileUpload(matterId);

  // Get correspondence data
  const allCorrespondence = useMemo(() => {
    return (
      externalMessages ||
      (matterId === 99
        ? mockCorrespondenceScenarios.empty
        : matterId === 180
          ? mockCorrespondenceScenarios.withDrafts.filter((c) =>
              institutionId ? c.institutionId === institutionId : true
            )
          : mockCorrespondenceScenarios.active.filter((c) =>
              institutionId ? c.institutionId === institutionId : true
            ))
    );
  }, [externalMessages, matterId, institutionId]);

  const existingDraftReply = useMemo(
    () =>
      allCorrespondence.find(
        (c) =>
          (c.type === 'reply' || c.type === 'follow-up') && c.status === 'draft'
      ),
    [allCorrespondence]
  );

  // Subject management
  const { messageSubject, setMessageSubject } =
    useSubjectManagement(allCorrespondence);

  // Attachment management (single instance, fully local staging)
  const attachments = useAttachments({
    uploadFile,
    refetchFiles,
    matterId
  });

  // Local draft persistence (localStorage with TTL)
  const {
    localDraftContent,
    localDraftAttachments,
    handleDraftChange,
    saveAttachments,
    clearLocalDraft
  } = useLocalDraft({ matterId, institutionId });

  // Draft management (detect existing backend draft only)
  const draftHook = useDraftManagement({
    existingDraftReply,
    pendingAttachments: attachments.pendingAttachments,
    onSetPendingAttachments: attachments.setPendingAttachments
  });

  // Sync attachments across localStorage and backend
  useDraftAttachmentSync({
    localDraftAttachments,
    pendingAttachments: attachments.pendingAttachments,
    setPendingAttachments: attachments.setPendingAttachments,
    saveAttachments,
    currentDraftId: draftHook.currentDraftId,
    onSaveDraftAttachments
  });

  // Message transformation
  const { messages, unreadCount } = useMessageTransform(
    allCorrespondence,
    institutionName
  );

  // Message sending
  const {
    isSendingMessage,
    optimisticMessage,
    clearOptimisticMessage,
    handleSendMessage
  } = useMessageSending({
    allCorrespondence,
    messageSubject,
    pendingAttachments: attachments.pendingAttachments,
    existingDraftId: draftHook.currentDraftId,
    onSendMessage: onSendMessageProp,
    onClearAttachments: attachments.clearAttachments,
    onClearDraft: draftHook.clearDraft
  });

  // Wrap send to also clear local draft
  const handleSendWithDraftClear = useCallback(
    async (content: string) => {
      await handleSendMessage(content);
      clearLocalDraft();
    },
    [handleSendMessage, clearLocalDraft]
  );

  // Display messages (real + optimistic)
  const displayMessages = useDisplayMessages({
    messages,
    optimisticMessage,
    clearOptimisticMessage
  });

  // Thread data
  const thread = useThreadData({
    messages: displayMessages,
    institutionName,
    institutionId,
    messageSubject,
    unreadCount
  });

  // Draft edit dialog
  const draftDialog = useDraftEditDialog({
    allCorrespondence,
    onUpdateDraft: onUpdateDraftProp,
    onDeleteDraft: onDeleteDraftProp
  });

  // Loading state
  if (!thread && isLoading) {
    return <ChatLoadingSkeleton fullHeight={fullHeight} />;
  }

  // Empty state
  if (!thread) {
    return (
      <EmptyState institutionName={institutionName} fullHeight={fullHeight} />
    );
  }

  // Main chat content
  const chatContent = (
    <div className={cn('flex flex-col', fullHeight ? 'h-full' : 'h-[600px]')}>
      <div className="flex-1 overflow-hidden">
        <MessageScrollArea
          thread={thread}
          visibleMessages={displayMessages.map((msg) => ({
            message: msg,
            thread
          }))}
          threadMap={new Map([[thread.id, thread]])}
          matterId={matterId}
          editingDraftId={draftDialog.editingDraftId}
          onSendDraft={onSendDraftProp || (async () => {})}
          onEditDraft={draftDialog.handleEditDraft}
          onDeleteDraft={draftDialog.handleDraftDelete}
          institutionFaviconUrl={institutionFaviconUrl}
        />
      </div>

      {!isSendingMessage && (
        <AttachmentsDisplay
          attachments={attachments.pendingAttachments}
          onRemove={attachments.handleRemoveAttachment}
        />
      )}

      <div className="shrink-0 border-t">
        <SubjectField
          subject={messageSubject}
          onSubjectChange={setMessageSubject}
          placeholder={`RE: Correspondence with ${institutionName}`}
        />
      </div>

      <div className="shrink-0 border-t">
        <ChatInput
          onSend={handleSendWithDraftClear}
          onChange={handleDraftChange}
          onAttach={attachments.handleAttachClick}
          placeholder={
            isSendingMessage ? 'Sending...' : `Message to ${institutionName}...`
          }
          disabled={isSendingMessage}
          isSending={isSendingMessage}
          autoFocus={autoFocus}
          initialValue={
            draftHook.draftContent || localDraftContent || initialMessage
          }
        />
      </div>

      <AddDocument
        matterId={matterId}
        open={attachments.showAttachmentDialog}
        onOpenChange={attachments.setShowAttachmentDialog}
        sources={['existing', 'upload', 'pms']}
        files={matterFiles ?? []}
        multiple
        allowCertifiedCopy
        alreadySelectedFileIds={attachments.pendingAttachments.map((a) => a.id)}
        onPickExisting={(ids, pickedFiles) => {
          // Attach the documents AddDocument resolved rather than re-finding them
          // in `availableDocuments`: a PMS import is brand new, so that lookup
          // would come back empty and silently attach nothing (LF-6261).
          attachments.handleAddAttachments(
            convertDocumentsToWorkflowFormat([...pickedFiles])
          );
          return ids;
        }}
        onUpload={async (files, _ctx, meta) => {
          await attachments.handleUploadDocument(
            files.map((file) => ({
              file,
              fileType: 'additional-document',
              isCertifiedCopy: meta?.isCertifiedCopy ?? false
            }))
          );
          return [];
        }}
      />
    </div>
  );

  // Fullscreen dialog
  if (isFullScreen) {
    return (
      <>
        {chatContent}
        <FullscreenDialog
          isOpen={isFullScreen}
          onClose={() => setIsFullScreen(false)}
          matterId={matterId}
          institutionId={institutionId}
          institutionName={institutionName}
          institutionFaviconUrl={institutionFaviconUrl}
          matterReference={matterReference}
          deceasedName={deceasedName}
          externalMessages={externalMessages}
          onSendMessage={onSendMessageProp}
          onSendDraft={onSendDraftProp}
          onUpdateDraft={onUpdateDraftProp}
          onDeleteDraft={onDeleteDraftProp}
          onSaveDraftAttachments={onSaveDraftAttachments}
          initialMessage={initialMessage}
        />
      </>
    );
  }

  return (
    <>
      {chatContent}
      {draftDialog.showEditDialog && (
        <EditDraftDialog
          open={draftDialog.showEditDialog}
          onOpenChange={draftDialog.setShowEditDialog}
          initialContent={draftDialog.editDraftContent}
          onSave={draftDialog.handleSaveEditedDraft}
        />
      )}
    </>
  );
}
